import { useCallback, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useEvent } from "@/lib/query/hooks/useEvent";
import { qk } from "@/lib/query/queryKeys";
import { updateEventDetailLayouts } from "@/lib/api/handlers/EventHandling";
import { isServerErr } from "@/lib/api/errors/ErrorHandler";
import { useViewsOptional } from "@/providers/ViewsProvider";
import {
  EDL_DEFAULT_VIEW_SCOPE,
  EDL_STANDARD,
  getEntityLayoutPref,
  setEntityLayoutPref,
  subscribeEntityLayoutPref,
  viewScopeOf,
} from "@/lib/storage/entityDetailLayoutPref";
import type {
  EntityDetailLayout,
  EventDetailLayouts,
  NamedEntityDetailLayout,
} from "@/types/Event";

/**
 * Fields-string of the detail-layout subscription — the ONE cache key every
 * consumer (both speaker mounts, both sponsor mounts, the config dialog, the
 * builder) reads and every post-save cache write targets, so a save lands in
 * every open surface at once. Mirrors LAYOUT_FIELDS in the guest layout
 * system, `updatedAt` included: it is the CAS witness for the whole-entity
 * write (see useSaveEntityDetailLayout).
 */
export const DETAIL_LAYOUT_FIELDS = "detailLayouts,updatedAt";

/** Entities whose detail popup is configurable (mirror of the backend's
 *  DETAIL_LAYOUT_VOCABULARY keys — an unknown entity is a 400).
 *
 *  The five guest-form entries are not popups of their own machinery. The three
 *  `*Create` ones are the admin CREATE forms; `registrantDetail` /
 *  `companionDetail` are the DECISION surfaces the requests page opens on a
 *  still-PENDING row. The create and decision lanes are separate documents on
 *  purpose — a create form gets trimmed to a quick-entry mask, and the same
 *  `hidden` list on the decision surface would hide the registrant's submitted
 *  answers from the approval (see {@link RequestDetailSurface}).
 *  They ride this storage (and this read/save pair) deliberately: the wire
 *  shape, the per-entity dot-path persist, the If-Match CAS and the
 *  merge-over-cache in {@link useSaveEntityDetailLayout} are exactly what a
 *  per-lane form layout needs, and reusing them avoided a new Event field with
 *  its ~10 field lists across two repos. Each layout of theirs carries
 *  `groups`/`hidden` only (both strip vocabularies are empty server-side), and
 *  `layouts` holds their named versions; see {@link CreateSurface} in
 *  `PGuestDetails/layout/createLayoutSurface.ts`. */
export type DetailLayoutEntity =
  | "speaker"
  | "sponsor"
  | "programItem"
  | "guestCreate"
  | "registrantCreate"
  | "companionCreate"
  | "registrantDetail"
  | "companionDetail";

/** One configurable strip and its display labels, as the dialog wants them. */
export type DetailStripKey = { id: string; label: string };

export { EDL_STANDARD };

/**
 * Read one entity's popup config off the shared event cache entry.
 *
 * `isPending` matters: painting the DEFAULT order while the config is still in
 * flight makes the rail visibly reshuffle (and config-hidden tabs flash) when
 * it lands — consumers hold their tabs until it resolves, exactly like
 * GuestNotes does.
 *
 * ⚠ `ready` is what any surface that WRITES the config must gate on. An absent
 * `layout` is ambiguous — it means both "this event has no config" and "the
 * read has not answered yet / failed" — so a config dialog opened before the
 * read lands seeds the DEFAULTS and its save then overwrites whatever was
 * stored. Only a SUCCEEDED read makes `layout === undefined` trustworthy.
 * (Reading is safe without it: the popup just shows the default order.)
 */
export function useEntityDetailLayout(
  eventId: string | null | undefined,
  entity: DetailLayoutEntity,
): {
  layout: EntityDetailLayout | undefined;
  /** The event's NAMED alternatives for this entity ([] when none). */
  named: NamedEntityDetailLayout[];
  /** CAS witness for the next write (event `updatedAt`, ISO instant). */
  ifMatch: string | undefined;
  isPending: boolean;
  /**
   * A refetch is in flight over data we already have. Distinct from
   * `isPending`: surfaces stay painted, but anything that would WRITE with the
   * current `ifMatch` must wait — the witness is about to be replaced, and
   * sending the old one races the refetch into a self-inflicted 409.
   */
  isFetching: boolean;
  ready: boolean;
  isError: boolean;
} {
  const layoutQ = useEvent(eventId ?? null, DETAIL_LAYOUT_FIELDS);
  const layout = layoutQ.data?.detailLayouts?.[entity];
  return {
    layout,
    named: layout?.layouts ?? [],
    ifMatch: layoutQ.data?.updatedAt
      ? new Date(layoutQ.data.updatedAt).toISOString()
      : undefined,
    isPending: !!eventId && layoutQ.isPending,
    isFetching: !!eventId && layoutQ.isFetching,
    // No eventId = nothing to read, so nothing to clobber either.
    ready: !eventId || layoutQ.isSuccess,
    // A FAILED read must not sit on a spinner forever: `ready` never turns true,
    // so a surface that only checks `ready` would hang. Callers that can edit the
    // config need to stop with a message instead.
    isError: layoutQ.isError,
  };
}

export type SelectedEntityLayout = {
  /** The layout to render. undefined = nothing stored → caller's default. */
  layout: EntityDetailLayout | undefined;
  /** EDL_STANDARD or the id of the selected named layout. */
  selectedId: string;
  source: "viewPref" | "viewLink" | "defaultPref" | "standard";
};

/**
 * Pure precedence engine for which layout a popup shows:
 *
 *   1. the user's pick for the CURRENT view context (a manual pick must beat
 *      the view's link, visibly, or the picker is a dead control),
 *   2. the active view's linked layout,
 *   3. the user's pick made outside any view context (DEFAULT scope — what
 *      the builder tab and standalone popups resolve through),
 *   4. the Standard layout.
 *
 * Every id is looked up against the CURRENT named list; unresolvable
 * (dangling) ids fall through silently — a deleted layout must never blank a
 * popup, and stale prefs/links are legal by design.
 */
export function selectEntityDetailLayout(args: {
  standard: EntityDetailLayout | undefined;
  named: NamedEntityDetailLayout[] | undefined;
  viewLinkedId: string | undefined;
  viewScopedPrefId: string | null;
  defaultScopedPrefId: string | null;
}): SelectedEntityLayout {
  // `?? []` is not decoration: this is a pure function reachable from callers
  // that spread a partially-loaded document, and a throw here would blank a
  // popup that has a perfectly good Standard layout to show.
  const named = args.named ?? [];
  const byId = (id: string) => named.find((l) => l.id === id);
  const candidates: Array<{
    id: string | null | undefined;
    source: SelectedEntityLayout["source"];
  }> = [
    { id: args.viewScopedPrefId, source: "viewPref" },
    { id: args.viewLinkedId, source: "viewLink" },
    { id: args.defaultScopedPrefId, source: "defaultPref" },
  ];

  for (const { id, source } of candidates) {
    if (!id) continue;
    if (id === EDL_STANDARD) {
      // An explicit "Standard" pick beats a view link — but only pref sources
      // can carry the sentinel (views link named layouts or nothing).
      return { layout: args.standard, selectedId: EDL_STANDARD, source };
    }
    const match = byId(id);
    if (match) return { layout: match, selectedId: match.id, source };
    // Dangling id → try the next precedence level.
  }
  return { layout: args.standard, selectedId: EDL_STANDARD, source: "standard" };
}

/**
 * Shared resolution hook for every consumer of an entity popup's layout.
 * Works inside a real ViewsProvider (the hub tables) and with none at all
 * (the builder page, the website-builder tab).
 */
export function useResolvedEntityDetailLayout(
  eventId: string | null | undefined,
  entity: DetailLayoutEntity,
): SelectedEntityLayout & {
  namedLayouts: NamedEntityDetailLayout[];
  /** The unnamed Standard layout, whatever is currently selected. */
  standard: EntityDetailLayout | undefined;
  isPending: boolean;
  /** See useEntityDetailLayout — gate WRITE controls on this, not on isPending. */
  isFetching: boolean;
  ready: boolean;
  isError: boolean;
  /** Persist a pick for the current view context and re-render this tab. */
  select: (layoutId: string) => void;
} {
  const doc = useEntityDetailLayout(eventId, entity);
  const views = useViewsOptional();
  const activeView = views?.activeViewState;
  const viewScope = viewScopeOf(activeView?.tableViewId);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!eventId) return () => {};
      // One subscription covers both scopes — over-notifying is harmless
      // (getSnapshot dedupes).
      const unsubView = subscribeEntityLayoutPref(
        entity,
        eventId,
        viewScope,
        onStoreChange,
      );
      const unsubDefault = subscribeEntityLayoutPref(
        entity,
        eventId,
        EDL_DEFAULT_VIEW_SCOPE,
        onStoreChange,
      );
      return () => {
        unsubView();
        unsubDefault();
      };
    },
    [entity, eventId, viewScope],
  );
  const viewScopedPrefId = useSyncExternalStore(subscribe, () =>
    eventId ? getEntityLayoutPref(entity, eventId, viewScope) : null,
  );
  const defaultScopedPrefId = useSyncExternalStore(subscribe, () =>
    eventId ? getEntityLayoutPref(entity, eventId, EDL_DEFAULT_VIEW_SCOPE) : null,
  );

  const selected = selectEntityDetailLayout({
    standard: doc.layout,
    named: doc.named,
    viewLinkedId: activeView?.detailLayoutId || undefined,
    viewScopedPrefId,
    defaultScopedPrefId,
  });

  const select = useCallback(
    (layoutId: string) => {
      if (!eventId) return;
      setEntityLayoutPref(entity, eventId, viewScope, layoutId);
    },
    [entity, eventId, viewScope],
  );

  return {
    ...selected,
    namedLayouts: doc.named,
    standard: doc.layout,
    isPending: doc.isPending,
    isFetching: doc.isFetching,
    ready: doc.ready,
    isError: doc.isError,
    select,
  };
}

/**
 * Persist ONE entity's popup config. The request carries only that entity —
 * the backend $sets a single dot-path, so the sibling entity's config survives
 * a concurrent edit from the other hub page. Resolves `true` on success.
 *
 * ⚠ Within the entity the object is REPLACED wholesale, so `patch` is merged
 * over the CURRENTLY CACHED object here: a caller that only knows about
 * `tabs`/`actions` can never drop `groups` or the whole `layouts` array.
 *
 * The event's `updatedAt` rides along as an If-Match witness. A concurrent
 * write then 409s instead of silently destroying the other tab's named
 * layout; `onStale` (optional) lets an editor show a conflict banner. The
 * return value stays a plain boolean so existing callers keep working —
 * a conflict is a failed save like any other from their point of view.
 */
export function useSaveEntityDetailLayout(
  eventId: string,
  entity: DetailLayoutEntity,
  options?: { onStale?: () => void },
): (patch: EntityDetailLayout) => Promise<boolean> {
  const queryClient = useQueryClient();
  const onStale = options?.onStale;
  // The SAME subscription every reader uses — deliberately not a second,
  // blind `getQueryData` peek: if the cache key ever drifted, that peek would
  // quietly return undefined, the merge below would stop merging, and the
  // first Standard save would delete every named layout without an error.
  // React Query dedupes, so this costs no extra request.
  const {
    layout: stored,
    ifMatch,
    ready,
  } = useEntityDetailLayout(eventId, entity);
  return useCallback(
    async (patch: EntityDetailLayout) => {
      const key = qk.events.detail(eventId, DETAIL_LAYOUT_FIELDS);

      // ⚠ Both guards protect the SAME merge, from two directions.
      //
      // `ready` — the merge below is the only thing standing between a
      // tabs-only save and the deletion of every named layout. Before the read
      // succeeds, `stored` is undefined for two indistinguishable reasons
      // ("no config" and "not answered yet"), so the merge silently degrades
      // into a wholesale replace. Refuse instead of writing blind.
      //
      // `ifMatch` — EventClient omits an absent If-Match header entirely, which
      // turns CAS OFF without a word. A save with no witness cannot detect the
      // concurrent write it is about to overwrite, so it must not go out.
      if (!ready || !ifMatch) return false;

      const next: EntityDetailLayout = { ...(stored ?? {}), ...patch };

      const res = await updateEventDetailLayouts(
        eventId,
        { [entity]: next },
        ifMatch,
      );
      if (isServerErr(res)) {
        if (res.message === "staleIfMatch") {
          // AWAIT, like the success path: the caller's very next act after a
          // conflict banner is usually "save again", and an un-awaited refetch
          // leaves this callback holding the SAME stale witness — a second 409
          // that looks like the server is broken.
          await queryClient.invalidateQueries({
            queryKey: ["events", "detail", eventId],
          });
          onStale?.();
        }
        return false;
      }
      // Optimistic write into the shared entry so open popups re-render at
      // once. AWAIT the invalidate: the write above carries no new
      // `updatedAt`, so a second save fired before the refetch lands would
      // send a stale witness and 409 against its own previous save.
      queryClient.setQueryData(
        key,
        (prev: { detailLayouts?: EventDetailLayouts } | undefined) => ({
          ...(prev ?? {}),
          detailLayouts: { ...(prev?.detailLayouts ?? {}), [entity]: next },
        }),
      );
      await queryClient.invalidateQueries({
        queryKey: ["events", "detail", eventId],
      });
      return true;
    },
    [eventId, entity, queryClient, onStale, stored, ifMatch, ready],
  );
}
